import { esc, linkify, cohortIcons, avatarHtml, displayName, timeAgo } from "./utils.js";
import { followButtonHtml, attachFollowHandlers } from "./followButton.js";
import { reactionsBarHtml, attachReactionHandlers } from "./reactions.js";
import { navigate } from "./app.js";
import { getCurrentUser } from "./auth.js";
import { unitsOf } from "./units.js";
import { diveProfileHtml, attachDiveProfiles } from "./diveProfileChart.js";

/**
 * Renders a post as a clickable card. Used by every view that lists posts
 * (feed, community detail, dive plan/site detail, user profile) so the
 * dive-site and community tags render consistently everywhere, mirroring
 * the Flutter app's single shared PostCard widget.
 */
/** Thumbnail strip for a post's photos. Caps at four with a "+N" overlay
 * on the last one — the full set is on the post detail view, and a feed
 * of ten-photo posts would be unscrollable. Tolerates `photos` being
 * absent so a cached/older API response still renders.
 *
 * A thumbnail opens that photo's page. The "+N" tile deliberately does
 * not: it promises "there are N more", and the place to see them is the
 * post, so it falls through to the card's own click.
 *
 * Anchors rather than click handlers, so middle-click and "open in new
 * tab" behave. attachPostCardHandlers has to let them through. */
function photoStripHtml(photos) {
  if (!photos || photos.length === 0) return "";
  const shown = photos.slice(0, 4);
  const hidden = photos.length - shown.length;
  const thumbs = shown
    .map((photo, i) => {
      const isOverflowTile = hidden > 0 && i === shown.length - 1;
      const img = `<img src="${esc(photo.image_url)}" alt="" loading="lazy"
               class="rounded border w-100 h-100" style="object-fit:cover;" />`;
      if (isOverflowTile) {
        return `
        <div class="position-relative" style="width:84px;height:84px;">
          ${img}
          <div class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded text-white fw-semibold" style="background:rgba(0,0,0,.5);">+${hidden}</div>
        </div>`;
      }
      return `
        <a href="#/photo/${photo.id}" class="position-relative d-block post-photo"
           style="width:84px;height:84px;" aria-label="Open this photo">
          ${img}
        </a>`;
    })
    .join("");
  return `<div class="d-flex flex-wrap gap-2 mb-2">${thumbs}</div>`;
}

export function postCardHtml(post) {
  const author = displayName(post.author);
  const chips = [];
  if (post.dive_plan) {
    const statusBadge = post.dive_plan.is_past
      ? `<span class="badge text-bg-secondary me-2">Past dive</span>`
      : `<span class="badge text-bg-primary me-2">Upcoming dive</span>`;
    chips.push(
      `<span class="badge text-bg-light border me-2 plan-chip" data-plan-id="${post.dive_plan.id}"><i class="bi bi-calendar-event me-1"></i>${esc(post.dive_plan.title)}</span>${statusBadge}`
    );
  }
  if (post.dive_site) {
    chips.push(
      `<span class="badge text-bg-light border me-2 site-chip" data-site-id="${post.dive_site.id}"><i class="bi bi-geo-alt me-1"></i>${esc(post.dive_site.name)}</span>`
    );
  }
  if (post.community) {
    chips.push(
      `<span class="badge text-bg-light border me-2 community-chip" data-community-id="${post.community.id}">${cohortIcons(post.community)}${esc(post.community.name)}</span>`
    );
  }
  return `
    <div class="card mb-3 card-dc-clickable" data-post-id="${post.id}">
      <div class="card-body">
        <div class="d-flex align-items-start mb-2">
          ${avatarHtml(author, { className: "me-2 author-link", attrs: `data-user-id="${post.author?.id ?? ""}"` })}
          <div>
            <div class="fw-semibold author-link" data-user-id="${post.author?.id ?? ""}">${esc(author)}</div>
            <div class="text-slate small">${timeAgo(post.created_at)}</div>
          </div>
          ${followButtonHtml(post.author)}
        </div>
        ${chips.length ? `<div class="mb-2">${chips.join("")}</div>` : ""}
        <p class="mb-2 dc-user-text">${linkify(post.content)}</p>
        ${post.dive_profile ? diveProfileHtml(post.dive_profile, { units: unitsOf(getCurrentUser()) }) : ""}
        ${photoStripHtml(post.photos)}
        <!-- One footer row: the comment count, then the reactions
             immediately to its right. Both sit left; the row does not
             stretch them apart. The bar may render empty (signed out,
             no reactions yet), which just leaves the count on its own. -->
        <div class="d-flex align-items-center gap-3 post-card-footer">
          <div class="text-slate small"><i class="bi bi-chat me-1"></i>${post.comment_count}</div>
          ${reactionsBarHtml({ postId: post.id }, post.reactions)}
        </div>
      </div>
    </div>`;
}

/** Wires up click handlers on a container of postCardHtml() cards: the
 * card itself opens the post, the author opens their profile, the
 * community chip opens that community, and the dive-event chip opens
 * that dive plan — all without triggering each other (stopPropagation
 * on the inner targets). */
export function attachPostCardHandlers(container) {
  container.querySelectorAll("[data-post-id]").forEach((card) => {
    card.addEventListener("click", (e) => {
      if (
        e.target.closest(".dc-user-link") || // a link someone wrote opens the site, not the post
        e.target.closest(".author-link") ||
        e.target.closest(".community-chip") ||
        e.target.closest(".site-chip") ||
        e.target.closest(".post-photo") ||
        e.target.closest(".plan-chip") ||
        e.target.closest(".follow-btn") ||
        e.target.closest(".reactions") ||
        e.target.closest(".dive-units-switch")
      )
        return;
      navigate(`/post/${card.dataset.postId}`);
    });
  });
  container.querySelectorAll(".author-link").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      if (el.dataset.userId) navigate(`/user/${el.dataset.userId}`);
    });
  });
  container.querySelectorAll(".community-chip").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      navigate(`/cohort/${el.dataset.communityId}`);
    });
  });
  attachFollowHandlers(container);
  attachReactionHandlers(container);
  attachDiveProfiles(container);
  container.querySelectorAll(".site-chip").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      navigate(`/dive-site/${el.dataset.siteId}`);
    });
  });
  container.querySelectorAll(".plan-chip").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      navigate(`/dive-plan/${el.dataset.planId}`);
    });
  });
}
